// src/components/GuessNumberGame.jsx
import React, { useState, useEffect } from "react";

function GuessNumberGame() {
  const [randomNumber, setRandomNumber] = useState(null);
  const [guess, setGuess] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 3;

  useEffect(() => {
    setLoading(true);
    setError(null);
    setMessage("");
    setAttempts(0);
    setGuess("");
    setName("");

    const timer = setTimeout(() => {
      try {
        const shouldFail = Math.random() < 0.1;
        if (shouldFail) throw new Error("Failed to load lucky number!");
        const number = Math.floor(Math.random() * 10) + 1;
        setRandomNumber(number);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, []); // run once on mount

  const checkGuess = () => {
    setMessage("");
    if (!name.trim()) {
      setMessage("Please enter your name first!");
      return;
    }

    if (attempts >= maxAttempts) {
      setMessage(`No more attempts left, ${name}! Better luck next time.`);
      return;
    }

    const value = parseInt(guess, 10);
    if (Number.isNaN(value) || value < 1 || value > 10) {
      setMessage("Please enter a valid number between 1 and 10.");
      return;
    }

    setAttempts((prev) => prev + 1);

    if (value === randomNumber) {
      setMessage(`Success, ${name}! You guessed the lucky number 🎉`);
    } else if (attempts + 1 === maxAttempts) {
      setMessage(
        `Sorry, ${name}! No attempts left. The lucky number was ${randomNumber}.`
      );
    } else {
      const remaining = maxAttempts - (attempts + 1);
      setMessage(`Wrong guess, ${name}! Try again — ${remaining} attempt(s) left.`);
    }

    setGuess("");
  };

  const handleReset = () => {
    // regenerate number and reset state
    setLoading(true);
    setError(null);
    setMessage("");
    setAttempts(0);
    setGuess("");
    setName("");
    setTimeout(() => {
      const number = Math.floor(Math.random() * 10) + 1;
      setRandomNumber(number);
      setLoading(false);
    }, 400);
  };

  if (loading)
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse text-2xl font-semibold text-gray-600">Loading lucky number...</div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-md">
          <h3 className="font-semibold">Error</h3>
          <p className="mt-2">{error}</p>
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );

  // Main UI
  const outOfAttempts = attempts >= maxAttempts;

  return (
    <div className="max-w-xl mx-auto p-6 mt-12 bg-white rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold text-gray-800 text-center">🔢 Guess the Lucky Number</h2>
      <p className="text-center text-sm text-gray-500 mt-1">Pick a number between 1 and 10 — you have {maxAttempts} attempts!</p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your guess</label>
          <div className="flex gap-3">
            <input
              type="number"
              min="1"
              max="10"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              placeholder="1 - 10"
              className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
              disabled={outOfAttempts}
            />
            <button
              onClick={checkGuess}
              disabled={outOfAttempts}
              className={`px-4 py-2 rounded-lg text-white font-medium transition ${
                outOfAttempts
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              Guess
            </button>
          </div>
        </div>

        <div className="text-center">
          <p
            className={`inline-block px-4 py-2 rounded-md ${
              message.toLowerCase().includes("success")
                ? "bg-green-50 border border-green-200 text-green-800"
                : message.toLowerCase().includes("sorry") || message.toLowerCase().includes("error")
                ? "bg-red-50 border border-red-200 text-red-800"
                : "bg-yellow-50 border border-yellow-200 text-yellow-800"
            }`}
          >
            {message || "Good luck!"}
          </p>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>Attempts: <span className="font-medium text-gray-800">{attempts}</span> / {maxAttempts}</div>
          <div>
            <button
              onClick={handleReset}
              className="px-3 py-1 bg-gray-100 rounded-md hover:bg-gray-200 transition"
            >
              Reset Game
            </button>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-400 text-center">
          (Hint: number is between 1 and 10)
        </div>
      </div>
    </div>
  );
}

export default GuessNumberGame;
